// Copyright (c) 2024 Deere & Company

import './responsible-dealer-report.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import Criteria from '../state/criteria';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import GeneralSettings from '@deere/cps-it.warranty-model-factory/lib/general-settings';
import InlineAlertBar from '../../../shared/components/inline-alert-bar/index';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ResponsibleDealerCriteria from './responsible-dealer-report-criteria';
import ResponsibleDealerReport from '@deere/cps-it.warranty-model-factory/lib/responsible-dealer-report';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import View from '../state/view';
import WarrantyAgGridDatePicker from '../../shared/components/ag-grid-date-picker';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {getResponsibleDealerColumnDefinitions} from '../report-services/column-definitions';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleModelInputChange(event) {

    const model = event.target.value;

    this.actions.updateResponsibleDealerCriteria('model', model);

}

function handleLatestInputChange(event) {

    const isLatest = event.target.value;

    this.actions.updateResponsibleDealerCriteria('isLatest', isLatest);

}

function handleDealerSelect(index, isSelected) {

    this.actions.selectResponsibleDealer(index, isSelected);

}

function handleSelectAllDealers() {

    this.actions.selectAllResponsibleDealers(this.selectedAccounts);

}

function handleCreateReport() {

    this.actions.toggleReportIsScrollable();
    this.actions.resetSearchResults();
    this.actions.createResponsibleReport(false);

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleExportToCsv() {

    this.actions.createResponsibleReport(true);

}

function handleReset() {

    this.actions.resetCriteria(this.selectedAccounts);
    this.actions.resetSearchResults();
    this.actions.updateShowNoResultsFound(false);

}

class ResponsibleDealerReports extends Component {

    componentWillMount() {

        this.props.actions.selectAllDealersOnLoad(this.props.selectedAccounts);
        this.props.actions.getReportLayouts('responsible-dealer');

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();

            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetSearchResults();
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        const gridOptions = {
            dateComponentFramework: WarrantyAgGridDatePicker
        };

        return (
            <section className='responsible-dealer'>
                <h2 className='responsible-dealer-title'>
                    <FormattedMessage id='RESPONSIBLE_DEALER'/>
                    <FavoriteIcon
                        id='RESPONSIBLE_DEALER'
                        route={this.props.pathName}
                    />
                </h2>
                <ResponsibleDealerCriteria
                    canExport={this.props.report.length > 0}
                    canRunReport={this.props.selectedDealers.length > 0}
                    isLatest={this.props.criteria.isLatest}
                    model={this.props.criteria.model}
                    onCreateReport={this.props::handleCreateReport}
                    onDealerSelect={this.props::handleDealerSelect}
                    onExportToCsv={this.props::handleExportToCsv}
                    onLatestInputChange={this.props::handleLatestInputChange}
                    onModelInputChange={this.props::handleModelInputChange}
                    onReset={this.props::handleReset}
                    onSelectAllDealers={this.props::handleSelectAllDealers}
                    selectedAccounts={this.props.selectedAccounts}
                    selectedDealers={this.props.selectedDealers}
                />
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <InlineAlertBar
                            label='RESPONSIBLE_DEALER_REPORT_RECORDS_COUNT_WARNING'
                            type='warning'
                        />
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='responsible-dealer'
                                responsibleExportToCSV={this.props::handleExportToCsv}
                                showReportLayout={false}
                                showResponsibleDealer={true}
                            />
                            <ReportGrid
                                columnDefs={getResponsibleDealerColumnDefinitions()}
                                enableFilter={true}
                                gridOptions={gridOptions}
                                headerHeight='32'
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                rowData={this.props.report}
                                rowHeight='32'
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

ResponsibleDealerReports.displayName = 'ResponsibleDealerReports';
ResponsibleDealerReports.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    generalSettings: PropTypes.instanceOf(GeneralSettings).isRequired,
    gridApi: PropTypes.object.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(ResponsibleDealerReport)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedDealers: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired,
    views: PropTypes.arrayOf(PropTypes.instanceOf(View)).isRequired
};

export default ResponsibleDealerReports;



// WEBPACK FOOTER //
// ./src/reports/responsible-dealer/components/responsible-dealer-report.js